import * as THREE from 'three';
import { DigitalObject } from '../../rendering/binary/DigitalObject.js';
import { DigitalSign } from './DigitalSign.js';
import { combineSeed, verticalRoadPatch } from './cityLayout.js';
import {
  blockStride,
  horizontalRoadCenterZ,
  rangeOverlaps,
  verticalRoadCenterX,
} from '../streaming/worldGrid.js';

/**
 * @param {{
 *   material: import('../../rendering/binary/BinaryMaterial.js').BinaryMaterial;
 *   config: import('../../config.js').CONFIG['city'];
 *   roadRoot: THREE.Group;
 *   spacing?: number;
 *   getCellIdBase: () => number;
 *   onPlaced: (info: { cellsUsed: number; maxColumns: number; drawCalls: number }) => void;
 * }} ctx
 */
export function buildStreetLights(ctx) {
  const {
    material,
    poleMaterial = material,
    config,
    roadRoot,
    spacing = 32,
    getCellIdBase,
    onPlaced,
  } = ctx;
  const { blockCount, roadWidth, sidewalkWidth } = config;
  const lights = new THREE.Group();
  lights.name = 'StreetLights';

  let placed = 0;
  const maxLights = 88;

  for (let i = 0; i <= blockCount && placed < maxLights; i += 1) {
    const vPatch = verticalRoadPatch(i, config);
    const edgeX = vPatch.centerX + roadWidth / 2 + sidewalkWidth + 0.35;
    const span = vPatch.depth;
    const startZ = vPatch.centerZ - span / 2 + spacing * 0.5;

    for (let z = startZ; z < vPatch.centerZ + span / 2 && placed < maxLights; z += spacing) {
      const x =
        placed % 2 === 0
          ? edgeX
          : edgeX - (roadWidth + sidewalkWidth * 2 + 0.7);
      addLight(x, z);
      placed += 1;
    }
  }

  roadRoot.add(lights);

  function addLight(x, z) {
    _addStreetLamp({
      x,
      z,
      material,
      poleMaterial,
      config,
      lights,
      getCellIdBase,
      onPlaced,
    });
  }
}

/**
 * Street lamps along global road grid clipped to a streaming chunk.
 */
export function buildStreetLightsInBounds(ctx) {
  const {
    bounds,
    material,
    poleMaterial = material,
    config,
    roadRoot,
    spacing = 28,
    maxLights = 22,
    getCellIdBase,
    onPlaced,
  } = ctx;
  const { roadWidth, sidewalkWidth } = config;
  const stride = blockStride(config);
  const lights = new THREE.Group();
  lights.name = 'StreetLights';
  let placed = 0;

  const iMin = Math.floor((bounds.minX - roadWidth / 2) / stride) - 1;
  const iMax = Math.ceil((bounds.maxX - roadWidth / 2) / stride) + 1;
  const jMin = Math.floor((bounds.minZ - roadWidth / 2) / stride) - 1;
  const jMax = Math.ceil((bounds.maxZ - roadWidth / 2) / stride) + 1;

  for (let i = iMin; i <= iMax && placed < maxLights; i += 1) {
    const roadX = verticalRoadCenterX(i, config);
    if (
      !rangeOverlaps(
        roadX - roadWidth / 2,
        roadX + roadWidth / 2,
        bounds.minX,
        bounds.maxX,
      )
    ) {
      continue;
    }
    const edgeX = roadX + roadWidth / 2 + sidewalkWidth + 0.35;
    const altX = roadX - roadWidth / 2 - sidewalkWidth - 0.35;
    for (let z = bounds.minZ + spacing * 0.5; z < bounds.maxZ && placed < maxLights; z += spacing) {
      const x = placed % 2 === 0 ? edgeX : altX;
      _addStreetLamp({
        x,
        z,
        material,
        poleMaterial,
        config,
        lights,
        getCellIdBase,
        onPlaced,
      });
      placed += 1;
    }
  }

  for (let j = jMin; j <= jMax && placed < maxLights; j += 1) {
    const roadZ = horizontalRoadCenterZ(j, config);
    if (
      !rangeOverlaps(
        roadZ - roadWidth / 2,
        roadZ + roadWidth / 2,
        bounds.minZ,
        bounds.maxZ,
      )
    ) {
      continue;
    }
    const edgeZ = roadZ + roadWidth / 2 + sidewalkWidth + 0.35;
    const altZ = roadZ - roadWidth / 2 - sidewalkWidth - 0.35;
    for (let x = bounds.minX + spacing * 0.5; x < bounds.maxX && placed < maxLights; x += spacing) {
      const z = placed % 2 === 0 ? edgeZ : altZ;
      _addStreetLamp({
        x,
        z,
        material,
        poleMaterial,
        config,
        lights,
        getCellIdBase,
        onPlaced,
      });
      placed += 1;
    }
  }

  if (placed > 0) {
    roadRoot.add(lights);
  }
}

/**
 * @param {{
 *   x: number;
 *   z: number;
 *   material: import('../../rendering/binary/BinaryMaterial.js').BinaryMaterial;
 *   config: import('../../config.js').CONFIG['city'];
 *   lights: THREE.Group;
 *   getCellIdBase: () => number;
 *   onPlaced: (info: { cellsUsed: number; maxColumns: number; drawCalls: number }) => void;
 * }} params
 */
function _addStreetLamp(params) {
  const {
    x,
    z,
    material,
    poleMaterial = material,
    config,
    lights,
    getCellIdBase,
    onPlaced,
  } = params;
  const cellIdBase = getCellIdBase();
  const seed = combineSeed(config.seed, Math.floor(x * 3), Math.floor(z * 3), 880);
  const group = new THREE.Group();
  group.position.set(x, 0, z);

  const poleHeight = 4.35;
  const pole = new DigitalObject({
    material: poleMaterial,
    shape: 'pillar',
    size: [0.24, poleHeight, 0.24],
    position: [0, poleHeight / 2, 0],
    cellsPerMeter: 7,
    seed: seed + 1,
    cellIdBase,
    name: 'StreetLampPole',
  });
  group.add(pole.group);

  const headCellBase = cellIdBase + pole.cellsUsed;
  const head = new DigitalObject({
    material,
    shape: 'box',
    size: [0.72, 0.36, 0.36],
    position: [0, poleHeight + 0.2, 0],
    cellsPerMeter: 9,
    seed,
    cellIdBase: headCellBase,
    name: 'StreetLampHead',
  });
  group.add(head.group);
  lights.add(group);

  const poleCols = pole.surfaces.reduce((m, s) => Math.max(m, s.columns), 1);
  const headCols = head.surfaces.reduce((m, s) => Math.max(m, s.columns), 1);

  onPlaced({
    cellsUsed: pole.cellsUsed + head.cellsUsed,
    maxColumns: Math.max(poleCols, headCols),
    drawCalls: pole.drawCalls + head.drawCalls,
  });
}

/**
 * @param {{
 *   material: import('../../rendering/binary/BinaryMaterial.js').BinaryMaterial;
 *   centerX: number;
 *   centerZ: number;
 *   height: number;
 *   seed: number;
 *   cellIdBase: number;
 *   signRegistry: DigitalSign[];
 * }} opts
 */
export function buildAlleyDetail(opts) {
  const group = new THREE.Group();
  group.name = 'AlleyDetail';

  const pipe = new DigitalObject({
    material: opts.material,
    shape: 'pillar',
    size: [0.28, opts.height * 0.55, 0.28],
    position: [opts.centerX, opts.height * 0.28, opts.centerZ],
    cellsPerMeter: 5,
    seed: opts.seed + 50,
    cellIdBase: opts.cellIdBase,
    name: 'AlleyPipe',
  });
  group.add(pipe.group);

  let cellNext = opts.cellIdBase + pipe.cellsUsed;
  let drawCalls = pipe.drawCalls;
  let maxColumns = pipe.surfaces.reduce((m, s) => Math.max(m, s.columns), 1);

  const sign = new DigitalSign({
    width: 1.4,
    height: 0.42,
    seed: opts.seed + 77,
    neon: true,
    phrases: ['ACCESS DENIED', '010101010101', 'WAKE UP'],
  });
  sign.mesh.position.set(opts.centerX, opts.height * 0.55, opts.centerZ);
  sign.mesh.rotation.y = Math.PI / 2;
  group.add(sign.mesh);
  opts.signRegistry.push(sign);

  return {
    group,
    cellsUsed: cellNext - opts.cellIdBase,
    drawCalls,
    maxColumns,
    signs: [sign],
  };
}
